import { useState, useCallback } from "react";
import { useCreate, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, X, Loader2, Calendar } from "lucide-react";

interface EstimateCreateDialogProps {
  open: boolean;
  onClose: () => void;
  contactId: string | number;
  contactName: string;
  /** Called with the new estimate ID after successful creation */
  onCreated?: (estimateId: string) => void;
}

interface FormData {
  title: string;
  description: string;
  estimated_days: string;
  valid_until: string;
  tax_rate: string;
}

const INITIAL: FormData = {
  title: "",
  description: "",
  estimated_days: "",
  valid_until: "",
  tax_rate: "0",
};

export function EstimateCreateDialog({
  open,
  onClose,
  contactId,
  contactName,
  onCreated,
}: EstimateCreateDialogProps) {
  const [form, setForm] = useState<FormData>(INITIAL);
  const [saving, setSaving] = useState(false);
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();

  const update = (field: keyof FormData, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = useCallback(async () => {
    if (!form.title.trim()) {
      notify("Title is required", { type: "warning" });
      return;
    }

    setSaving(true);
    try {
      const estimateData: Record<string, unknown> = {
        contact_id: contactId,
        title: form.title.trim(),
        description: form.description.trim(),
        estimated_days: form.estimated_days
          ? parseInt(form.estimated_days, 10)
          : null,
        valid_until: form.valid_until || null,
        tax_rate: parseFloat(form.tax_rate) / 100 || 0,
      };

      const { data: newEstimate } = await create(
        "estimates",
        { data: estimateData },
        { returnPromise: true }
      );

      // Create timeline event
      if (newEstimate?.id) {
        await create(
          "events",
          {
            data: {
              contact_id: contactId,
              event_type: "estimate_created",
              payload: {
                estimate_id: newEstimate.id,
                estimate_number: (newEstimate as Record<string, unknown>)
                  .estimate_number,
                title: form.title.trim(),
                contact_name: contactName,
              },
            },
          },
          { returnPromise: true }
        );
      }

      notify("Estimate created", { type: "success" });
      setForm(INITIAL);
      refresh();
      onCreated?.(newEstimate?.id as string);
      onClose();
    } catch (err) {
      notify(`Failed to create estimate: ${(err as Error).message}`, {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }, [form, contactId, contactName, create, notify, refresh, onCreated, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardContent className="p-0">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4" />
              New Estimate
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Contact context */}
          <div className="px-4 pt-3">
            <p className="text-xs text-muted-foreground">
              Creating estimate for{" "}
              <span className="font-medium text-foreground">
                {contactName}
              </span>
            </p>
          </div>

          {/* Form */}
          <div className="p-4 space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
                <FileText className="w-3 h-3" />
                Title *
              </span>
              <input
                type="text"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                placeholder="e.g. Kitchen renovation estimate"
                autoFocus
                className="w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-muted-foreground mb-1 block">
                Description
              </span>
              <textarea
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                placeholder="Scope of work, assumptions, exclusions…"
                rows={3}
                className="w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </label>

            <div className="flex gap-3">
              <label className="flex-1">
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
                  <Calendar className="w-3 h-3" />
                  Est. duration (days)
                </span>
                <input
                  type="number"
                  min={1}
                  value={form.estimated_days}
                  onChange={(e) => update("estimated_days", e.target.value)}
                  placeholder="e.g. 14"
                  className="w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </label>
              <label className="flex-1">
                <span className="text-xs font-medium text-muted-foreground mb-1 block">
                  Valid until
                </span>
                <input
                  type="date"
                  value={form.valid_until}
                  onChange={(e) => update("valid_until", e.target.value)}
                  className="w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs font-medium text-muted-foreground mb-1 block">
                Tax rate (%)
              </span>
              <input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={form.tax_rate}
                onChange={(e) => update("tax_rate", e.target.value)}
                placeholder="0"
                className="w-32 px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </label>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 rounded text-sm font-medium border text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || !form.title.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FileText className="w-3.5 h-3.5" />
              )}
              {saving ? "Creating…" : "Create Estimate"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
